/**
 * Medição mensal de um credenciado, como a SME a acompanha nas abas do
 * "Ciclo mensal" (`/sme/medicoes` e `/sme/demonstrativos`).
 *
 * O formato segue o do protótipo (`medicoes`, com credenciado, DRE e termo
 * embutidos pela consulta), traduzido para os nomes em português usados no
 * resto de `modulos/financeiro`. O demonstrativo não é outra entidade na tela:
 * é o espelho formal da medição a partir de `CALCULADA`, por isso vem junto.
 */

export const STATUS_MEDICAO_SME = [
  'RASCUNHO',
  'PROCESSADA',
  'CALCULADA',
  'APROVADA',
  'EMPENHADA',
  'EXPORTADA_SOF',
  'LIQUIDADA',
  'PAGA',
  'REJEITADA',
  'CANCELADA',
] as const;

export type StatusMedicaoSme = (typeof STATUS_MEDICAO_SME)[number];

/** Só a partir daqui a medição tem demonstrativo — mesma lista do protótipo. */
export const STATUS_COM_DEMONSTRATIVO: readonly StatusMedicaoSme[] = [
  'CALCULADA',
  'APROVADA',
  'EMPENHADA',
  'EXPORTADA_SOF',
  'LIQUIDADA',
  'PAGA',
];

/** PJ inclui cooperativa, como o seletor "PJ / Coop." do protótipo. */
export type TipoPessoaSme = 'PF' | 'PJ';

export type TipoDocumentoLiquidacao =
  'NOTA_FISCAL' | 'RECIBO_RPA' | 'CND_FEDERAL' | 'CRF_FGTS' | 'CNDT_TRABALHISTA';

export interface AlertaFiscal {
  nivel: 'CRITICO' | 'ATENCAO';
  mensagem: string;
}

export interface DreDaMedicao {
  id: string;
  codigo: string;
  nome: string;
}

export interface MedicaoSme {
  id: string;
  /** Competência `yyyy-mm`. */
  mesReferencia: string;
  status: StatusMedicaoSme;
  /** Decimal em texto, como o `numeric(14,2)` chega da API. */
  valorTotal: string;
  termoSei: string | null;
  credenciado: {
    id: string;
    nome: string;
    cpfCnpj: string;
    tipo: TipoPessoaSme;
  };
  dre: DreDaMedicao;
  /** Demonstrativo devolvido pelo credenciado — alimenta o selo da aba. */
  recusadoPeloCredenciado: boolean;
  documentosPendentes: readonly TipoDocumentoLiquidacao[];
  alertaFiscal: AlertaFiscal | null;
}
